import { Heading, IconButton, Text } from '@/atoms';
import { useServerPageConfig } from '@/contexts/config';
import { useSearchV2 } from '@/contexts/search/AddSearchProviderV2';
import { ThemeProvider } from '@/contexts/theme';
import { Env } from '@/server/config/types';
import { getThemeInfo } from '@/themes';
import { cls } from '@/utils';
import { Hit } from 'addsearch-js-client';
import { useKeyPress } from 'hooks/useKeyPress';
import { CSSProperties, useEffect, useRef, useState } from 'react';
import { RemoveScroll } from 'react-remove-scroll';
import classNames from 'classnames';
import { t } from 'ttag';
import styles from './SearchWidgetV2.module.scss';
import { SearchField } from './components/SearchField';
import { SearchResultItem } from './components/SearchResultItem';

export const SearchWidgetV2: React.FC = () => {
	const theme = 'searchWidget';
	const themeInfo = getThemeInfo(theme);
	const { searchTerm, addSearchClient, searchResult, isSearchOpen, setSearchOpen, noSearchResults } = useSearchV2();
	const { env, externalDeployment } = useServerPageConfig();
	const isExternalProdEnvironment = env === Env.Prod && externalDeployment;
	const [isFadingOut, setIsFadingOut] = useState(false);
	const fadeOutTimer = useRef<NodeJS.Timeout | null>(null);
	const fadeOutDuration = 300;

	const style = {
		'--fade-out-duration': `${fadeOutDuration}ms`,
	} as CSSProperties;

	useKeyPress('Escape', undefined, () => {
		setSearchOpen(false);
	});

	const handleClick = (hit: Hit, index: number) => {
		if (isExternalProdEnvironment && addSearchClient)
			addSearchClient.sendStatsEvent('click', searchTerm, { documentId: hit.id, position: index });
	};

	const handleClose = () => {
		setIsFadingOut(true);
		if (fadeOutTimer.current) clearTimeout(fadeOutTimer.current);
		fadeOutTimer.current = setTimeout(() => {
			setSearchOpen(false);
			setIsFadingOut(false);
		}, fadeOutDuration);
	};

	useEffect(() => {
		return () => {
			if (fadeOutTimer.current) clearTimeout(fadeOutTimer.current);
		};
	}, []);

	return (
		<RemoveScroll enabled={isSearchOpen}>
			<ThemeProvider theme={theme}>
				<div
					className={classNames(styles.container, themeInfo.styles.bgColor, isSearchOpen && styles.open)}
					role="search"
					style={style}
				>
					<div className={cls(styles.inner, isFadingOut && styles.fadeOut)}>
						<div className={styles.closeBtnWrapper}>
							<IconButton
								iconName="xmark"
								aria-label={t`Stäng sökruta`}
								iconSize="2x"
								onClick={handleClose}
								className={styles.desktop}
							/>
							<IconButton
								iconName="xmark"
								aria-label={t`Stäng sökruta`}
								onClick={handleClose}
								iconSize="lg"
								className={cls(styles.closeBtn, styles.mobile)}
							/>
						</div>

						<div className={styles.titleWrapper}>
							<Heading as="div" size="h5" title={t`Sök på Fortnox`} marginBottom="2xl" />
						</div>

						<SearchField />

						<div className={styles.text}>
							{noSearchResults && <Text>{t`Vi hittade tyvärr inget sökresultat.`}</Text>}
						</div>

						<div className={styles.searchResult}>
							{searchResult?.map((hit: Hit, index: number) => (
								<SearchResultItem key={hit.id} hit={hit} onClick={() => handleClick(hit, index + 1)} />
							))}
						</div>
					</div>
				</div>
			</ThemeProvider>
		</RemoveScroll>
	);
};
